import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import {
    UsersIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    AcademicCapIcon,
    BriefcaseIcon,
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    MapPinIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowTopRightOnSquareIcon,
    BuildingLibraryIcon,
    DocumentTextIcon,
    IdentificationIcon,
    SparklesIcon,
    ClockIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';

export default function AdminUserInfo() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Selected user for modal detail view
    const [selectedUser, setSelectedUser] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [fullDetails, setFullDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/users', { params: { page: 1, limit: 300 } });
            const payload = res.data.data;
            setUsers(payload?.users || []);
        } catch (err) {
            toast.error('Failed to load user directory');
        } finally {
            setLoading(false);
        }
    };

    // Calculate metrics
    const metrics = useMemo(() => {
        const total = users.length;
        let interns = 0;
        let mentors = 0;
        let staff = 0;

        users.forEach((u) => {
            const roleNames = (u.Roles || u.roles || []).map((r) => (typeof r === 'string' ? r : r.name));
            if (roleNames.includes('intern')) interns++;
            if (roleNames.includes('mentor')) mentors++;
            if (roleNames.some((r) => ['super_admin', 'program_manager', 'trainer', 'admin'].includes(r))) staff++;
        });

        return { total, interns, mentors, staff };
    }, [users]);

    // Filter users
    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users.filter((u) => {
            const roleNames = (u.Roles || u.roles || []).map((r) => (typeof r === 'string' ? r : r.name));
            
            // Filter by role
            if (roleFilter !== 'all') {
                if (roleFilter === 'intern' && !roleNames.includes('intern')) return false;
                if (roleFilter === 'mentor' && !roleNames.includes('mentor')) return false;
                if (roleFilter === 'admin' && !roleNames.some((r) => ['super_admin', 'program_manager', 'admin'].includes(r))) return false;
                if (roleFilter === 'trainer' && !roleNames.includes('trainer')) return false;
            }

            // Filter by account active status
            if (statusFilter === 'active' && !u.is_active) return false;
            if (statusFilter === 'inactive' && u.is_active) return false;
            if (statusFilter === 'unverified' && u.is_email_verified) return false;

            // Search query
            if (!q) return true;
            const profile = u.profile || {};
            const haystack = [
                u.first_name,
                u.last_name,
                u.email,
                u.phone,
                profile.university,
                profile.major,
                profile.student_id,
                ...roleNames
            ].filter(Boolean).join(' ').toLowerCase();

            return haystack.includes(q);
        });
    }, [users, search, roleFilter, statusFilter]);

    // Handle card click to open full details
    const handleCardClick = async (user) => {
        setSelectedUser(user);
        setFullDetails(null);
        setActiveTab('overview');
        setDetailsLoading(true);

        try {
            const res = await axios.get(`/users/${user.id}/full-details`);
            if (res.data.success) {
                setFullDetails(res.data.data);
            } else {
                toast.error('Could not fetch user details');
            }
        } catch (err) {
            console.error('Fetch full details error:', err);
            setFullDetails({
                user: user,
                roles: user.Roles || [],
                profile: user.profile || null,
                onboardingDraft: null
            });
            toast.error('Loaded basic user details');
        } finally {
            setDetailsLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedUser(null);
        setFullDetails(null);
    };

    const getRoleBadgeColor = (roleName) => {
        switch (roleName) {
            case 'super_admin':
            case 'admin':
                return 'bg-rose-50 text-rose-700 border-rose-200';
            case 'program_manager':
                return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'mentor':
                return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'trainer':
                return 'bg-sky-50 text-sky-700 border-sky-200';
            case 'intern':
            default:
                return 'bg-brand-50 text-brand-800 border-brand-200';
        }
    };

    const getInitials = (firstName, lastName) => {
        const f = firstName ? firstName.charAt(0).toUpperCase() : '';
        const l = lastName ? lastName.charAt(0).toUpperCase() : '';
        return (f + l) || 'U';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return String(dateStr);
        }
    };

    // Helper to resolve working document web URLs from server paths
    const resolveDocUrl = (rawPath) => {
        if (!rawPath || typeof rawPath !== 'string') return null;
        if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) return rawPath;

        const norm = rawPath.replace(/\\/g, '/');
        const match = norm.match(/\/uploads\/(.+)$/i);
        if (match) {
            return `/uploads/${match[1]}`;
        }
        if (norm.startsWith('/uploads/')) return norm;
        if (norm.startsWith('/')) return norm;
        return `/uploads/${norm}`;
    };

    // Helper to smartly parse and render nested JSON section values
    const renderSmartValue = (val) => {
        if (val === null || val === undefined || val === '') {
            return <span className="text-ink-400 italic text-xs">Not provided / N/A</span>;
        }

        if (typeof val === 'boolean') {
            return (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${val ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-surface text-ink-600 border border-border'}`}>
                    {val ? 'Yes' : 'No'}
                </span>
            );
        }

        if (typeof val === 'number') {
            return <span className="font-semibold text-ink-950 text-sm">{val}</span>;
        }

        let parsed = val;
        if (typeof val === 'string' && (val.trim().startsWith('{') || val.trim().startsWith('['))) {
            try {
                parsed = JSON.parse(val);
            } catch {
                parsed = val;
            }
        }

        // 1. Unwrap meta keys & wrapper objects (e.g. { entries: [...] }, { answers: {...} }, { consents: {...} })
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            const keys = Object.keys(parsed).filter(
                (k) => !['is_complete', 'isComplete', 'complete', 'skipped', 'is_skipped'].includes(k)
            );

            if (keys.length === 1 && ['entries', 'answers', 'consents', 'data', 'skills', 'items', 'responses'].includes(keys[0])) {
                parsed = parsed[keys[0]];
            }
        }

        // If stringified JSON returned after unwrapping
        if (typeof parsed === 'string' && (parsed.trim().startsWith('{') || parsed.trim().startsWith('['))) {
            try {
                parsed = JSON.parse(parsed);
            } catch {}
        }

        // If array of items
        if (Array.isArray(parsed)) {
            if (parsed.length === 0) {
                return <span className="text-ink-400 italic text-xs">None listed</span>;
            }

            const first = parsed[0];

            // Check if items are Education objects
            const isEducationList = typeof first === 'object' && first !== null && (
                first.institution || first.school || first.field_of_study || first.passing_year || first.grade_value || first.level
            );

            if (isEducationList) {
                return (
                    <div className="space-y-4 w-full">
                        {parsed.map((edu, idx) => {
                            const inst = edu.institution || edu.school || 'University / Institution';
                            const field = edu.field_of_study || edu.major || edu.degree || 'Degree Program';
                            const level = edu.level || edu.degree_level;
                            const passing = edu.passing_year || edu.graduation_year || edu.end_date;
                            const gradeVal = edu.grade_value || edu.grade || edu.gpa;
                            const gradeType = edu.grade_type;

                            return (
                                <div key={idx} className="p-4 bg-white border border-border rounded-xl space-y-3 shadow-xs">
                                    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border/60 pb-2.5">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <AcademicCapIcon className="w-5 h-5 text-brand-700" />
                                                <h5 className="font-bold text-sm text-ink-950 capitalize">
                                                    {level ? `${level} - ` : ''}{field}
                                                </h5>
                                            </div>
                                            <p className="text-xs font-semibold text-brand-700 mt-0.5 flex items-center gap-1">
                                                <BuildingLibraryIcon className="w-3.5 h-3.5" />
                                                {inst}
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {edu.is_in_progress && (
                                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                                    In Progress
                                                </span>
                                            )}
                                            {passing && (
                                                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-surface text-ink-700 border border-border">
                                                    Class of {passing}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-xs pt-1">
                                        {gradeVal && (
                                            <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                <span className="text-[11px] font-medium text-ink-500 block">Grade / GPA</span>
                                                <span className="font-bold text-ink-950 text-sm">{gradeVal}</span>
                                            </div>
                                        )}
                                        {gradeType && (
                                            <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                <span className="text-[11px] font-medium text-ink-500 block">Grade Type</span>
                                                <span className="font-semibold text-ink-900 capitalize">{String(gradeType).replace(/_/g, ' ')}</span>
                                            </div>
                                        )}
                                        {edu.board && (
                                            <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                <span className="text-[11px] font-medium text-ink-500 block">Board / Authority</span>
                                                <span className="font-semibold text-ink-900">{edu.board}</span>
                                            </div>
                                        )}
                                        {edu.id && (
                                            <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                <span className="text-[11px] font-medium text-ink-500 block">Entry ID</span>
                                                <span className="font-mono text-[11px] text-ink-600 truncate block">{edu.id}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // Check if items are Work Experience objects
            const isExperienceList = typeof first === 'object' && first !== null && (
                first.role_title || first.role || first.company || first.organization || first.start_date || first.startDate
            );

            if (isExperienceList) {
                return (
                    <div className="space-y-4 w-full">
                        {parsed.map((exp, idx) => {
                            const title = exp.role_title || exp.title || exp.role || 'Position / Role';
                            const company = exp.company || exp.organization || exp.employer;
                            const start = formatDate(exp.start_date || exp.startDate);
                            const end = exp.is_current || exp.isCurrent ? 'Present' : formatDate(exp.end_date || exp.endDate);
                            const desc = exp.description;

                            return (
                                <div key={idx} className="p-4 bg-white border border-border rounded-xl space-y-3 shadow-xs">
                                    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border/60 pb-2.5">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <BriefcaseIcon className="w-5 h-5 text-brand-700" />
                                                <h5 className="font-bold text-sm text-ink-950">
                                                    {title}
                                                </h5>
                                            </div>
                                            {company && (
                                                <p className="text-xs font-semibold text-brand-700 mt-0.5">
                                                    {company} {exp.location ? `• ${exp.location}` : ''}
                                                </p>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {exp.is_current && (
                                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    Current Role
                                                </span>
                                            )}
                                            {(exp.start_date || exp.end_date) && (
                                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface text-ink-600 border border-border">
                                                    {start} - {end}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {desc && (
                                        <div className="bg-surface/60 p-3 rounded-lg border border-border/50 text-xs text-ink-800 leading-relaxed whitespace-pre-line">
                                            {desc}
                                        </div>
                                    )}

                                    <div className="flex flex-wrap items-center gap-4 text-xs text-ink-500 pt-1">
                                        {exp.link_url && (
                                            <a href={resolveDocUrl(exp.link_url)} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline flex items-center gap-1 font-semibold">
                                                Project Link <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                            </a>
                                        )}
                                        {exp.id && (
                                            <span className="font-mono text-[11px] text-ink-400">ID: {exp.id}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // Check if items are Skill objects
            const isSkillList = typeof first === 'object' && first !== null && (first.skill_name || first.name || first.category);

            if (isSkillList) {
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
                        {parsed.map((item, idx) => {
                            const name = item.skill_name || item.name || item.title || 'Skill';
                            const category = item.category || item.type;
                            const level = item.proficiency !== undefined ? item.proficiency : item.proficiency_level !== undefined ? item.proficiency_level : item.level;

                            return (
                                <div key={idx} className="p-3 bg-white border border-border hover:border-brand-200 rounded-xl flex items-center justify-between gap-3 shadow-xs">
                                    <div>
                                        <div className="font-bold text-sm text-ink-950">{name}</div>
                                        {category && (
                                            <span className="inline-block mt-0.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-brand-50 text-brand-800 border border-brand-100">
                                                {category}
                                            </span>
                                        )}
                                    </div>
                                    {level !== undefined && level !== null && (
                                        <div className="text-right shrink-0">
                                            <div className="text-[10px] uppercase font-bold text-brand-700 tracking-wider">Level</div>
                                            <div className="text-xs font-bold text-ink-900 bg-brand-50 px-2 py-0.5 rounded border border-brand-100">{level} / 5</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // Generic Array of objects (Label on top, Value below!)
            if (typeof first === 'object' && first !== null) {
                return (
                    <div className="space-y-3 w-full">
                        {parsed.map((item, idx) => (
                            <div key={idx} className="p-4 bg-white border border-border rounded-xl space-y-3 shadow-xs">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-xs">
                                    {Object.entries(item).map(([k, v]) => {
                                        if (['is_complete', 'isComplete', 'complete', 'skipped'].includes(k)) return null;
                                        return (
                                            <div key={k} className="bg-surface p-2.5 rounded-lg border border-border">
                                                <span className="text-[11px] font-bold text-ink-500 uppercase tracking-wider block mb-0.5">
                                                    {k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                                                </span>
                                                <div className="font-semibold text-ink-950 break-words">
                                                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            }

            // Array of simple values/strings
            return (
                <div className="flex flex-wrap gap-2 w-full">
                    {parsed.map((item, idx) => (
                        <span key={idx} className="px-3 py-1 bg-brand-50 text-brand-800 border border-brand-200 rounded-lg text-xs font-semibold shadow-xs">
                            {String(item)}
                        </span>
                    ))}
                </div>
            );
        }

        // Key-value object
        if (typeof parsed === 'object' && parsed !== null) {
            const entries = Object.entries(parsed).filter(
                ([k]) => !['is_complete', 'isComplete', 'complete', 'skipped', 'is_skipped'].includes(k)
            );

            if (entries.length === 0) {
                return <span className="text-ink-400 italic text-xs">No entries</span>;
            }

            // Check if all values are booleans or status strings like "Yes"/"No" (Consents section)
            const isConsentsObj = entries.every(([, v]) => typeof v === 'boolean' || ['yes', 'no', 'true', 'false'].includes(String(v).toLowerCase()));
            if (isConsentsObj) {
                return (
                    <div className="flex flex-wrap gap-3 w-full">
                        {entries.map(([k, v]) => {
                            const isYes = v === true || String(v).toLowerCase() === 'yes' || String(v).toLowerCase() === 'true';
                            return (
                                <div key={k} className="flex items-center gap-2.5 px-4 py-2.5 bg-white border border-border rounded-xl shadow-xs">
                                    <span className="font-semibold text-xs text-ink-800 capitalize">
                                        {k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}:
                                    </span>
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${isYes ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`}>
                                        {isYes ? '✓ Yes' : '✕ No'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // Check if all values are numerical scores e.g. 1 to 5 (Work preferences / personality ratings)
            const isNumericalRatings = entries.every(([, v]) => typeof v === 'number' || (!isNaN(Number(v)) && Number(v) >= 1 && Number(v) <= 10 && String(v).trim().length <= 2));
            if (isNumericalRatings) {
                return (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 w-full">
                        {entries.map(([k, v]) => (
                            <div key={k} className="p-3 bg-white border border-border rounded-xl flex items-center justify-between gap-2 shadow-xs">
                                <span className="text-xs font-bold text-ink-800 capitalize truncate">
                                    {k.replace(/_/g, ' ')}
                                </span>
                                <div className="px-2.5 py-1 bg-brand-50 text-brand-900 border border-brand-200 rounded-lg text-xs font-bold shrink-0">
                                    {v} / 5
                                </div>
                            </div>
                        ))}
                    </div>
                );
            }

            // Check if object represents multiple choice / scenarios answers (short question keys, short answer string values)
            const isScenariosObj = entries.every(([, v]) => typeof v === 'string' && v.length < 50);
            if (isScenariosObj) {
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 w-full">
                        {entries.map(([q, ans]) => (
                            <div key={q} className="p-3.5 bg-white border border-border rounded-xl space-y-1.5 shadow-xs">
                                <span className="text-xs font-bold text-ink-700 block line-clamp-2">
                                    {q.replace(/_/g, ' ')}
                                </span>
                                <div className="inline-block px-3 py-1 bg-brand-50 text-brand-900 border border-brand-200 rounded-lg text-xs font-bold capitalize">
                                    {String(ans).replace(/_/g, ' ')}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            }

            // Standard object grid (Label on top, Value below!)
            return (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 w-full">
                    {entries.map(([k, v]) => (
                        <div key={k} className="p-3 bg-surface border border-border rounded-xl text-xs space-y-1">
                            <span className="text-[11px] font-bold text-ink-500 uppercase tracking-wider block">
                                {k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                            </span>
                            <div className="font-semibold text-ink-950 break-words mt-0.5">
                                {renderSmartValue(v)}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return <span className="text-ink-950 font-medium leading-relaxed whitespace-pre-line text-xs">{String(parsed)}</span>;
    };

    // Render Section Card with minimal header badge and full-width body
    const renderSectionCard = (sectionKey, sectionData) => {
        let actualData = sectionData;
        let isComplete = null;
        let isSkipped = false;

        if (typeof sectionData === 'object' && sectionData !== null) {
            if ('data' in sectionData) {
                actualData = sectionData.data;
            }
            if ('is_complete' in sectionData) isComplete = sectionData.is_complete;
            if ('isComplete' in sectionData) isComplete = sectionData.isComplete;
            if ('complete' in sectionData) isComplete = sectionData.complete;
            if ('skipped' in sectionData) isSkipped = sectionData.skipped;
        }

        // Try parsing stringified actualData
        if (typeof actualData === 'string' && (actualData.trim().startsWith('{') || actualData.trim().startsWith('['))) {
            try {
                actualData = JSON.parse(actualData);
            } catch {}
        }

        return (
            <div key={sectionKey} className="card p-5 border border-border shadow-sm space-y-4">
                {/* Header with Title and Minimal Status Badge */}
                <div className="flex items-center justify-between border-b border-border pb-3">
                    <h5 className="font-bold text-sm text-brand-900 uppercase tracking-wider flex items-center gap-2">
                        <SparklesIcon className="w-4 h-4 text-brand-700" />
                        Section: {sectionKey.replace(/_/g, ' ')}
                    </h5>

                    <div className="flex items-center gap-2">
                        {isSkipped ? (
                            <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[11px] font-semibold">
                                Skipped
                            </span>
                        ) : isComplete !== null ? (
                            <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${isComplete ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-surface text-ink-600 border-border'}`}>
                                {isComplete ? '✓ Completed' : 'In Progress'}
                            </span>
                        ) : null}
                    </div>
                </div>

                {/* Content taking full 100% width */}
                <div className="w-full">
                    {renderSmartValue(actualData)}
                </div>
            </div>
        );
    };

    // Unified Document List for selected user
    const allDocs = useMemo(() => {
        if (!fullDetails) return [];
        const list = [];
        const seenUrls = new Set();

        // 1. Draft documents
        if (fullDetails.onboardingDraft?.documents) {
            fullDetails.onboardingDraft.documents.forEach((d) => {
                const url = resolveDocUrl(d.url || d.file_path);
                if (url && !seenUrls.has(url)) {
                    seenUrls.add(url);
                    list.push({
                        id: d.id || d.file_path,
                        type: d.document_type || 'Onboarding Document',
                        name: d.original_name || d.document_type || 'Uploaded File',
                        size: d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB` : 'N/A',
                        url
                    });
                }
            });
        }

        // 2. Profile Resume URL
        if (fullDetails.profile?.resume_url) {
            const url = resolveDocUrl(fullDetails.profile.resume_url);
            if (url && !seenUrls.has(url)) {
                seenUrls.add(url);
                list.push({
                    id: 'resume',
                    type: 'CV / Resume',
                    name: 'Intern Resume / Curriculum Vitae',
                    size: 'Profile Attached',
                    url
                });
            }
        }

        return list;
    }, [fullDetails]);

    return (
        <div className="w-full space-y-6 pb-12">
            <PageHeader
                title="User Info & Directory"
                description="Comprehensive directory of all platform users. Click any user card to inspect stored profile, academic history, and onboarding form data."
            />

            {/* Metrics Row (Project Brand Colors) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card p-4 flex items-center gap-4 border border-border hover:shadow-card-hover transition-all">
                    <div className="p-3 bg-brand-50 text-brand-700 rounded-xl">
                        <UsersIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-ink-950">{metrics.total}</div>
                        <div className="text-xs font-medium text-ink-500">Total Users</div>
                    </div>
                </div>

                <div className="card p-4 flex items-center gap-4 border border-border hover:shadow-card-hover transition-all">
                    <div className="p-3 bg-brand-50 text-brand-700 rounded-xl">
                        <AcademicCapIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-ink-950">{metrics.interns}</div>
                        <div className="text-xs font-medium text-ink-500">Intern Accounts</div>
                    </div>
                </div>

                <div className="card p-4 flex items-center gap-4 border border-border hover:shadow-card-hover transition-all">
                    <div className="p-3 bg-amber-50 text-amber-700 rounded-xl">
                        <BriefcaseIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-ink-950">{metrics.mentors}</div>
                        <div className="text-xs font-medium text-ink-500">Mentors & Coaches</div>
                    </div>
                </div>

                <div className="card p-4 flex items-center gap-4 border border-border hover:shadow-card-hover transition-all">
                    <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
                        <ShieldCheckIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-ink-950">{metrics.staff}</div>
                        <div className="text-xs font-medium text-ink-500">Program Managers & Admins</div>
                    </div>
                </div>
            </div>

            {/* Controls Bar */}
            <div className="card p-4 flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
                {/* Search */}
                <div className="relative flex-1">
                    <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                    <input
                        type="text"
                        placeholder="Search by name, email, phone, university, major..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input-field pl-10 w-full"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600"
                        >
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider hidden lg:inline">Role:</span>
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="input-field w-auto text-sm py-2"
                    >
                        <option value="all">All Roles</option>
                        <option value="intern">Interns</option>
                        <option value="mentor">Mentors</option>
                        <option value="admin">Admins & PMs</option>
                        <option value="trainer">Trainers</option>
                    </select>

                    <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider hidden lg:inline ml-2">Status:</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="input-field w-auto text-sm py-2"
                    >
                        <option value="all">All Statuses</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive</option>
                        <option value="unverified">Unverified Email</option>
                    </select>
                </div>
            </div>

            {/* Directory Cards Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="card p-6 space-y-4 animate-pulse">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-ink-100 rounded-full" />
                                <div className="space-y-2 flex-1">
                                    <div className="h-4 bg-ink-200 rounded w-3/4" />
                                    <div className="h-3 bg-ink-150 rounded w-1/2" />
                                </div>
                            </div>
                            <div className="h-16 bg-ink-100 rounded-lg" />
                        </div>
                    ))}
                </div>
            ) : filteredUsers.length === 0 ? (
                <EmptyState
                    icon={IdentificationIcon}
                    title="No users found"
                    description="Try adjusting your search criteria or role filters."
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredUsers.map((u) => {
                        const roles = (u.Roles || u.roles || []).map((r) => (typeof r === 'string' ? r : r.name));
                        const isIntern = roles.includes('intern');
                        const profile = u.profile || {};

                        return (
                            <div
                                key={u.id}
                                onClick={() => handleCardClick(u)}
                                className="card p-5 cursor-pointer border border-border hover:border-brand-300 hover:shadow-card-hover transition-all duration-200 flex flex-col justify-between group relative overflow-hidden"
                            >
                                <div className="space-y-4">
                                    {/* Header Info */}
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            {profile.profile_picture ? (
                                                <img
                                                    src={profile.profile_picture}
                                                    alt={u.first_name}
                                                    className="w-12 h-12 rounded-full object-cover border-2 border-brand-100"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-brand-700 text-white font-bold flex items-center justify-center text-lg shadow-sm">
                                                    {getInitials(u.first_name, u.last_name)}
                                                </div>
                                            )}
                                            <div>
                                                <h3 className="font-bold text-ink-950 group-hover:text-brand-700 transition-colors line-clamp-1">
                                                    {u.first_name} {u.last_name}
                                                </h3>
                                                <div className="text-xs text-ink-500 flex items-center gap-1 mt-0.5">
                                                    <EnvelopeIcon className="w-3.5 h-3.5" />
                                                    <span className="truncate max-w-[180px]">{u.email}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                                            {u.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    {/* Roles Badges */}
                                    <div className="flex flex-wrap gap-1.5">
                                        {roles.map((role) => (
                                            <span
                                                key={role}
                                                className={`px-2 py-0.5 text-[11px] font-medium rounded-md border uppercase tracking-wider ${getRoleBadgeColor(role)}`}
                                            >
                                                {role.replace('_', ' ')}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Major info preview */}
                                    <div className="bg-brand-50/50 rounded-xl p-3 text-xs space-y-1.5 border border-brand-100/60">
                                        {isIntern ? (
                                            <>
                                                <div className="flex justify-between items-center text-ink-700">
                                                    <span className="text-ink-500 flex items-center gap-1">
                                                        <BuildingLibraryIcon className="w-3.5 h-3.5" /> University:
                                                    </span>
                                                    <span className="font-medium truncate max-w-[150px]">{profile.university || 'Not set'}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-ink-700">
                                                    <span className="text-ink-500 flex items-center gap-1">
                                                        <AcademicCapIcon className="w-3.5 h-3.5" /> Major:
                                                    </span>
                                                    <span className="font-medium truncate max-w-[150px]">{profile.major || 'Not set'}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-ink-700 pt-1 border-t border-brand-100">
                                                    <span className="text-ink-500">Profile Completion:</span>
                                                    <span className="font-semibold text-brand-700">{profile.profile_completion_percentage || 0}%</span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex justify-between items-center text-ink-700">
                                                    <span className="text-ink-500 flex items-center gap-1">
                                                        <PhoneIcon className="w-3.5 h-3.5" /> Phone:
                                                    </span>
                                                    <span className="font-medium">{u.phone || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-ink-700">
                                                    <span className="text-ink-500 flex items-center gap-1">
                                                        <CalendarIcon className="w-3.5 h-3.5" /> Registered:
                                                    </span>
                                                    <span className="font-medium">{formatDate(u.created_at)}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-ink-700 pt-1 border-t border-brand-100">
                                                    <span className="text-ink-500">Email Status:</span>
                                                    <span className={`font-semibold ${u.is_email_verified ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                        {u.is_email_verified ? 'Verified' : 'Unverified'}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-brand-700 font-semibold group-hover:translate-x-1 transition-transform">
                                    <span>Click to view all stored details</span>
                                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* FULL DETAILS MODAL (Matching Brand Design Tokens) */}
            {selectedUser && (
                <div className="fixed inset-0 z-50 overflow-y-auto bg-ink-950/60 backdrop-blur-sm flex justify-center items-center p-4 sm:p-6 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                        
                        {/* Modal Header */}
                        <div className="p-6 bg-gradient-to-r from-ink-950 via-brand-950 to-brand-900 text-white flex justify-between items-start border-b border-brand-800">
                            <div className="flex items-center gap-4">
                                {selectedUser.profile?.profile_picture ? (
                                    <img
                                        src={selectedUser.profile.profile_picture}
                                        alt={selectedUser.first_name}
                                        className="w-16 h-16 rounded-full object-cover border-2 border-white/20 shadow"
                                    />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-brand-700 text-white font-bold flex items-center justify-center text-2xl shadow border border-white/20">
                                        {getInitials(selectedUser.first_name, selectedUser.last_name)}
                                    </div>
                                )}
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-2xl font-bold text-white">
                                            {selectedUser.first_name} {selectedUser.last_name}
                                        </h2>
                                        <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${selectedUser.is_active ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'}`}>
                                            {selectedUser.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    <div className="text-sm text-brand-100 mt-1 flex flex-wrap items-center gap-4">
                                        <span className="flex items-center gap-1">
                                            <EnvelopeIcon className="w-4 h-4" /> {selectedUser.email}
                                        </span>
                                        {selectedUser.phone && (
                                            <span className="flex items-center gap-1">
                                                <PhoneIcon className="w-4 h-4" /> {selectedUser.phone}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <CalendarIcon className="w-4 h-4" /> Joined: {formatDate(selectedUser.created_at)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={closeModal}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Navigation Tabs */}
                        <div className="bg-brand-50/50 px-6 border-b border-border flex flex-wrap gap-1 overflow-x-auto">
                            <button
                                type="button"
                                onClick={() => setActiveTab('overview')}
                                className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'overview' ? 'border-brand-700 text-brand-700 bg-white/60' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                            >
                                <IdentificationIcon className="w-4 h-4" /> Personal & Account Info
                            </button>
                            
                            <button
                                type="button"
                                onClick={() => setActiveTab('onboarding')}
                                className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'onboarding' ? 'border-brand-700 text-brand-700 bg-white/60' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                            >
                                <DocumentTextIcon className="w-4 h-4" /> Onboarding Form Submissions
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab('academic')}
                                className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'academic' ? 'border-brand-700 text-brand-700 bg-white/60' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                            >
                                <AcademicCapIcon className="w-4 h-4" /> Academic & Education
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab('experience')}
                                className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'experience' ? 'border-brand-700 text-brand-700 bg-white/60' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                            >
                                <BriefcaseIcon className="w-4 h-4" /> Skills & Experience
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab('career')}
                                className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'career' ? 'border-brand-700 text-brand-700 bg-white/60' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                            >
                                <SparklesIcon className="w-4 h-4" /> Career Goals & Links
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab('documents')}
                                className={`py-3 px-4 text-xs font-bold border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'documents' ? 'border-brand-700 text-brand-700 bg-white/60' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                            >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4" /> Uploaded Documents ({allDocs.length})
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                            {detailsLoading ? (
                                <div className="py-12 flex flex-col items-center justify-center space-y-3">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-700"></div>
                                    <p className="text-sm text-ink-500 font-medium">Fetching complete database records...</p>
                                </div>
                            ) : (
                                <>
                                    {/* TAB 1: OVERVIEW & PERSONAL INFO */}
                                    {activeTab === 'overview' && (
                                        <div className="space-y-6">
                                            {/* Account Summary */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <IdentificationIcon className="w-4 h-4 text-brand-700" /> Account Metadata
                                                </h4>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-surface p-4 rounded-xl border border-border">
                                                    <div>
                                                        <span className="text-xs text-ink-500 block">User ID</span>
                                                        <span className="text-sm font-semibold text-ink-900">#{selectedUser.id}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-ink-500 block">Assigned Roles</span>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {(selectedUser.Roles || selectedUser.roles || []).map((r) => (
                                                                <span key={typeof r === 'string' ? r : r.name} className="px-2 py-0.5 text-xs font-semibold rounded bg-brand-50 text-brand-800 border border-brand-100">
                                                                    {typeof r === 'string' ? r : r.name}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-ink-500 block">Email Verified</span>
                                                        <span className={`text-sm font-semibold ${selectedUser.is_email_verified ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                            {selectedUser.is_email_verified ? 'Verified (Yes)' : 'Unverified (No)'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-ink-500 block">Created At</span>
                                                        <span className="text-sm font-medium text-ink-900">{formatDate(selectedUser.created_at)}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-xs text-ink-500 block">Last Login</span>
                                                        <span className="text-sm font-medium text-ink-900">{formatDate(selectedUser.last_login)}</span>
                                                    </div>
                                                    {fullDetails?.profile && (
                                                        <div>
                                                            <span className="text-xs text-ink-500 block">Onboarding Status</span>
                                                            <span className="text-sm font-bold text-brand-700 uppercase">
                                                                {fullDetails.profile.onboarding_status || 'not_started'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Intern Detailed Personal Specs */}
                                            {fullDetails?.profile && (
                                                <>
                                                    <div>
                                                        <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                            <UserCircleIcon className="w-4 h-4 text-brand-700" /> Detailed Personal Specification
                                                        </h4>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-white p-4 rounded-xl border border-border shadow-card">
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Student ID</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.student_id || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Date of Birth</span>
                                                                <span className="text-sm font-medium text-ink-900">{formatDate(fullDetails.profile.date_of_birth)}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Gender</span>
                                                                <span className="text-sm font-medium text-ink-900 capitalize">{fullDetails.profile.gender || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Nationality</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.nationality || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Location / City</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.location || fullDetails.profile.city || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Country</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.country || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Address</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.address || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Postal Code</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.postal_code || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Visa Status</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.visa_status || 'N/A'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Has Own Transport</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.has_own_transport ? 'Yes' : 'No'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Willing to Relocate</span>
                                                                <span className="text-sm font-medium text-ink-900">{fullDetails.profile.willing_to_relocate ? 'Yes' : 'No'}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Emergency Contact */}
                                                    <div>
                                                        <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                            <PhoneIcon className="w-4 h-4 text-brand-700" /> Emergency Contact
                                                        </h4>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-amber-50/60 p-4 rounded-xl border border-amber-200/70">
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Contact Name</span>
                                                                <span className="text-sm font-semibold text-ink-900">{fullDetails.profile.emergency_contact_name || 'Not provided'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Contact Phone</span>
                                                                <span className="text-sm font-semibold text-ink-900">{fullDetails.profile.emergency_contact_phone || 'Not provided'}</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-ink-500 block">Relationship</span>
                                                                <span className="text-sm font-semibold text-ink-900">{fullDetails.profile.emergency_contact_relation || fullDetails.profile.emergency_contact_relationship || 'Not provided'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* TAB 2: ONBOARDING FORM SUBMISSIONS */}
                                    {activeTab === 'onboarding' && fullDetails?.profile && (
                                        <div className="space-y-6">
                                            {/* Header Banner */}
                                            <div className="bg-brand-50 p-4 rounded-xl border border-brand-100 flex flex-wrap items-center justify-between gap-4">
                                                <div>
                                                    <h4 className="font-bold text-brand-950">Onboarding Form & Profile Data Record</h4>
                                                    <p className="text-xs text-brand-700">Detailed responses filled during talent onboarding wizard and stored in the database</p>
                                                </div>
                                                {fullDetails.onboardingDraft ? (
                                                    <span className="px-3 py-1 bg-emerald-700 text-white font-semibold text-xs rounded-full shadow-sm">
                                                        Draft Status: {fullDetails.onboardingDraft.status} (Submitted: {formatDate(fullDetails.onboardingDraft.submitted_at)})
                                                    </span>
                                                ) : (
                                                    <span className="px-3 py-1 bg-brand-700 text-white font-semibold text-xs rounded-full shadow-sm">
                                                        Profile Status: {fullDetails.profile.onboarding_status || 'Active'}
                                                    </span>
                                                )}
                                            </div>

                                             {/* Render Form Sections with Smart Full-Width Formatting */}
                                             {fullDetails.onboardingDraft?.sections && Object.keys(fullDetails.onboardingDraft.sections).length > 0 && (
                                                 <div className="space-y-5">
                                                     <h5 className="text-xs font-bold text-ink-950 uppercase tracking-wider">Submitted Form Sections & Draft Answers</h5>
                                                     {Object.entries(fullDetails.onboardingDraft.sections).map(([sectionKey, sectionData]) => (
                                                         renderSectionCard(sectionKey, sectionData)
                                                     ))}
                                                 </div>
                                             )}

                                            {/* Structured Onboarding Form Data Summary (Always Available for Interns) */}
                                            <div className="space-y-4">
                                                <h5 className="text-xs font-bold text-ink-950 uppercase tracking-wider">
                                                    Structured Form Submissions & Stored Profile Answers
                                                </h5>

                                                {/* Section 1: Basic Contact Specs */}
                                                <div className="card p-4 border border-border space-y-3">
                                                    <h5 className="font-bold text-sm text-brand-700 uppercase tracking-wider border-b border-border pb-2">
                                                        1. Personal Contact & Verification Specs
                                                    </h5>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Full Name:</span>
                                                            <span className="font-semibold text-ink-950">{selectedUser.first_name} {selectedUser.last_name}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Email Address:</span>
                                                            <span className="font-semibold text-ink-950">{selectedUser.email}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Phone Number:</span>
                                                            <span className="font-semibold text-ink-950">{selectedUser.phone || 'Not provided'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Date of Birth:</span>
                                                            <span className="font-semibold text-ink-950">{formatDate(fullDetails.profile.date_of_birth)}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Gender:</span>
                                                            <span className="font-semibold text-ink-950 capitalize">{fullDetails.profile.gender || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Nationality:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.nationality || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">City / Location:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.city || fullDetails.profile.location || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Country:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.country || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Visa Status:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.visa_status || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Section 2: Academic & University Specs */}
                                                <div className="card p-4 border border-border space-y-3">
                                                    <h5 className="font-bold text-sm text-brand-700 uppercase tracking-wider border-b border-border pb-2">
                                                        2. Academic & University Onboarding Answers
                                                    </h5>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">University / Institution:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.university || 'Not specified'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Student ID Number:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.student_id || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Degree / Major:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.major || 'Not specified'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Graduation Year:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.graduation_year || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Current GPA:</span>
                                                            <span className="font-semibold text-ink-950">{fullDetails.profile.gpa || 'N/A'}</span>
                                                        </div>
                                                        <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                            <span className="text-ink-500 block font-medium">Expected Graduation Date:</span>
                                                            <span className="font-semibold text-ink-950">{formatDate(fullDetails.profile.expected_graduation)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Section 3: Aspirations & Preferences */}
                                                <div className="card p-4 border border-border space-y-3">
                                                    <h5 className="font-bold text-sm text-brand-700 uppercase tracking-wider border-b border-border pb-2">
                                                        3. Career Goals, AI Experience & Work Preferences
                                                    </h5>
                                                    <div className="space-y-3 text-xs">
                                                        <div className="bg-surface p-3 rounded-lg border border-border">
                                                            <span className="font-semibold text-ink-700 block mb-1">Professional Bio / Summary:</span>
                                                            <p className="text-ink-950 leading-relaxed whitespace-pre-line">{fullDetails.profile.bio || 'Not provided'}</p>
                                                        </div>
                                                        <div className="bg-surface p-3 rounded-lg border border-border">
                                                            <span className="font-semibold text-ink-700 block mb-1">Career Goals & Objectives:</span>
                                                            <p className="text-ink-950 leading-relaxed whitespace-pre-line">{fullDetails.profile.career_goals || 'Not provided'}</p>
                                                        </div>
                                                        <div className="bg-surface p-3 rounded-lg border border-border">
                                                            <span className="font-semibold text-ink-700 block mb-1">AI Experience & Background:</span>
                                                            <p className="text-ink-950 leading-relaxed whitespace-pre-line">{fullDetails.profile.ai_experience || 'Not provided'}</p>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                                <span className="text-ink-500 block font-medium">Preferred Work Type:</span>
                                                                <span className="font-semibold text-ink-950 capitalize">{fullDetails.profile.preferred_work_type || 'N/A'}</span>
                                                            </div>
                                                            <div className="bg-surface p-2.5 rounded-lg border border-border">
                                                                <span className="text-ink-500 block font-medium">Availability Window:</span>
                                                                <span className="font-semibold text-ink-950">
                                                                    {formatDate(fullDetails.profile.availability_start)} - {formatDate(fullDetails.profile.availability_end)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Uploaded Documents */}
                                            {fullDetails.onboardingDraft?.documents && fullDetails.onboardingDraft.documents.length > 0 && (
                                                <div>
                                                    <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3">
                                                        Uploaded Onboarding Documents
                                                    </h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {fullDetails.onboardingDraft.documents.map((doc) => (
                                                            <div key={doc.id} className="p-3 bg-white border border-border rounded-xl flex items-center justify-between text-xs">
                                                                <div>
                                                                    <div className="font-semibold text-ink-900">{doc.document_type || doc.file_name}</div>
                                                                    <div className="text-ink-500">{doc.file_size ? `${Math.round(doc.file_size / 1024)} KB` : 'Uploaded'}</div>
                                                                </div>
                                                                {doc.file_path && (
                                                                    <a href={doc.file_path} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline flex items-center gap-1 font-semibold">
                                                                        View <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* TAB 3: ACADEMIC & EDUCATION */}
                                    {activeTab === 'academic' && fullDetails?.profile && (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-brand-50/60 p-4 rounded-xl border border-brand-100">
                                                <div>
                                                    <span className="text-xs text-ink-500 block">University</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.university || 'Not specified'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">University ID</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.university_id || 'Not specified'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Major</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.major || 'Not specified'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Minor</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.minor || 'None'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Graduation Year</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.graduation_year || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">GPA</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.gpa || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Current Semester</span>
                                                    <span className="text-sm font-bold text-ink-900">{fullDetails.profile.current_semester || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Expected Graduation</span>
                                                    <span className="text-sm font-bold text-ink-900">{formatDate(fullDetails.profile.expected_graduation)}</span>
                                                </div>
                                            </div>

                                            {/* Full Education Records Table */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <AcademicCapIcon className="w-4 h-4 text-brand-700" /> Detailed Education History
                                                </h4>
                                                {fullDetails.profile.educations && fullDetails.profile.educations.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {fullDetails.profile.educations.map((edu) => (
                                                            <div key={edu.id} className="p-4 bg-white border border-border rounded-xl space-y-1">
                                                                <div className="flex justify-between items-start">
                                                                    <h5 className="font-bold text-sm text-ink-950">{edu.institution || edu.school}</h5>
                                                                    <span className="text-xs text-ink-500 font-medium">{formatDate(edu.start_date)} - {formatDate(edu.end_date) || 'Present'}</span>
                                                                </div>
                                                                <div className="text-xs text-brand-700 font-semibold">{edu.degree} in {edu.field_of_study || edu.major}</div>
                                                                {edu.grade && <div className="text-xs text-ink-500">Grade/GPA: {edu.grade}</div>}
                                                                {edu.description && <p className="text-xs text-ink-700 mt-2">{edu.description}</p>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="p-6 text-center text-xs text-ink-500 bg-surface rounded-xl border border-dashed border-border">
                                                        No additional education history entries recorded.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB 4: SKILLS, EXPERIENCE & CERTIFICATIONS */}
                                    {activeTab === 'experience' && fullDetails?.profile && (
                                        <div className="space-y-6">
                                            {/* Skills */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <SparklesIcon className="w-4 h-4 text-brand-700" /> Skills & Proficiencies
                                                </h4>
                                                {fullDetails.profile.skills && (Array.isArray(fullDetails.profile.skills) ? fullDetails.profile.skills.length > 0 : true) ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {(Array.isArray(fullDetails.profile.skills) ? fullDetails.profile.skills : []).map((sk, idx) => (
                                                            <span key={idx} className="px-3 py-1 bg-brand-50 text-brand-800 font-medium text-xs rounded-lg border border-brand-200 flex items-center gap-1.5">
                                                                <span>{typeof sk === 'string' ? sk : sk.name}</span>
                                                                {sk.proficiency_level && <span className="text-[10px] bg-brand-200 text-brand-900 px-1.5 py-0.5 rounded font-bold">{sk.proficiency_level}</span>}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-ink-500 bg-surface p-3 rounded-lg">No skills listed</div>
                                                )}
                                            </div>

                                            {/* Work Experience */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <BriefcaseIcon className="w-4 h-4 text-brand-700" /> Work Experience History
                                                </h4>
                                                {fullDetails.profile.workExperiences && fullDetails.profile.workExperiences.length > 0 ? (
                                                    <div className="space-y-3">
                                                        {fullDetails.profile.workExperiences.map((work) => (
                                                            <div key={work.id} className="p-4 bg-white border border-border rounded-xl space-y-1">
                                                                <div className="flex justify-between items-start">
                                                                    <h5 className="font-bold text-sm text-ink-950">{work.title || work.position}</h5>
                                                                    <span className="text-xs text-ink-500 font-medium">{formatDate(work.start_date)} - {formatDate(work.end_date) || 'Present'}</span>
                                                                </div>
                                                                <div className="text-xs text-brand-700 font-semibold">{work.company || work.organization} {work.location ? `• ${work.location}` : ''}</div>
                                                                {work.description && <p className="text-xs text-ink-700 mt-2 whitespace-pre-line">{work.description}</p>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="p-6 text-center text-xs text-ink-500 bg-surface rounded-xl border border-dashed border-border">
                                                        No work experience records added yet.
                                                    </div>
                                                )}
                                            </div>

                                            {/* Certificates */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                    <DocumentTextIcon className="w-4 h-4 text-brand-700" /> Certificates & Licenses
                                                </h4>
                                                {fullDetails.profile.certificates && fullDetails.profile.certificates.length > 0 ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {fullDetails.profile.certificates.map((cert) => (
                                                            <div key={cert.id} className="p-3 bg-white border border-border rounded-xl text-xs space-y-1">
                                                                <div className="font-bold text-ink-900">{cert.title || cert.name}</div>
                                                                <div className="text-brand-700 font-semibold">{cert.issuing_organization || cert.issuer}</div>
                                                                <div className="text-ink-500">Issued: {formatDate(cert.issue_date)}</div>
                                                                {cert.credential_id && <div className="text-ink-500">ID: {cert.credential_id}</div>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="p-4 text-xs text-ink-500 bg-surface rounded-lg">No external certificates logged</div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB 5: CAREER GOALS & LINKS */}
                                    {activeTab === 'career' && fullDetails?.profile && (
                                        <div className="space-y-6">
                                            {/* Bio */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-2">Professional Bio / Summary</h4>
                                                <div className="p-4 bg-surface rounded-xl border border-border text-xs text-ink-800 leading-relaxed whitespace-pre-line">
                                                    {fullDetails.profile.bio || 'No professional bio provided.'}
                                                </div>
                                            </div>

                                            {/* Career Goals */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-2">Career Goals</h4>
                                                <div className="p-4 bg-surface rounded-xl border border-border text-xs text-ink-800 leading-relaxed whitespace-pre-line">
                                                    {fullDetails.profile.career_goals || 'No career goals specified.'}
                                                </div>
                                            </div>

                                            {/* AI Experience */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-2">AI Experience & Background</h4>
                                                <div className="p-4 bg-surface rounded-xl border border-border text-xs text-ink-800 leading-relaxed whitespace-pre-line">
                                                    {fullDetails.profile.ai_experience || 'No AI experience details provided.'}
                                                </div>
                                            </div>

                                            {/* Preferences */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-brand-50/60 p-4 rounded-xl border border-brand-100">
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Preferred Work Type</span>
                                                    <span className="text-sm font-bold text-ink-900 capitalize">{fullDetails.profile.preferred_work_type || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-ink-500 block">Availability Start & End</span>
                                                    <span className="text-sm font-bold text-ink-900">
                                                        {formatDate(fullDetails.profile.availability_start)} - {formatDate(fullDetails.profile.availability_end)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Links */}
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-3">Portfolio & Social Links</h4>
                                                <div className="flex flex-wrap gap-3 text-xs">
                                                    {fullDetails.profile.linkedin_url && (
                                                        <a href={fullDetails.profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-blue-50 text-blue-700 font-semibold rounded-lg border border-blue-200 flex items-center gap-1.5 hover:bg-blue-100 transition-colors">
                                                            <GlobeAltIcon className="w-4 h-4" /> LinkedIn Profile <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    {fullDetails.profile.github_url && (
                                                        <a href={fullDetails.profile.github_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-ink-900 text-white font-semibold rounded-lg flex items-center gap-1.5 hover:bg-black transition-colors">
                                                            <GlobeAltIcon className="w-4 h-4" /> GitHub Profile <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    {fullDetails.profile.portfolio_url && (
                                                        <a href={fullDetails.profile.portfolio_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-brand-50 text-brand-800 font-semibold rounded-lg border border-brand-200 flex items-center gap-1.5 hover:bg-brand-100 transition-colors">
                                                            <GlobeAltIcon className="w-4 h-4" /> Portfolio Website <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    {!fullDetails.profile.linkedin_url && !fullDetails.profile.github_url && !fullDetails.profile.portfolio_url && (
                                                        <div className="text-xs text-ink-500">No external links recorded.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB 6: UPLOADED DOCUMENTS & CVS */}
                                    {activeTab === 'documents' && (
                                        <div className="space-y-6">
                                            <div>
                                                <h4 className="text-xs font-bold text-ink-950 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                    <DocumentTextIcon className="w-4 h-4 text-brand-700" /> Uploaded Documents & CVs ({allDocs.length})
                                                </h4>
                                                <p className="text-xs text-ink-500 mb-4">Click any document to view or download directly from the server.</p>

                                                {allDocs.length > 0 ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {allDocs.map((doc) => (
                                                            <div key={doc.id} className="p-4 bg-white border border-border hover:border-brand-300 rounded-xl flex items-center justify-between gap-4 shadow-sm hover:shadow-card-hover transition-all">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="p-3 bg-brand-50 text-brand-700 rounded-lg">
                                                                        <DocumentTextIcon className="w-6 h-6" />
                                                                    </div>
                                                                    <div>
                                                                        <h5 className="font-bold text-sm text-ink-950 capitalize">{doc.type}</h5>
                                                                        <p className="text-xs text-ink-500 truncate max-w-[200px]">{doc.name}</p>
                                                                        <span className="text-[11px] text-brand-700 font-medium">{doc.size}</span>
                                                                    </div>
                                                                </div>

                                                                <a
                                                                    href={doc.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="px-3 py-1.5 bg-brand-700 text-white font-semibold text-xs rounded-lg hover:bg-brand-800 transition-colors flex items-center gap-1.5 shadow-sm shrink-0"
                                                                >
                                                                    <span>Open File</span>
                                                                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                                                                </a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="p-8 text-center bg-surface rounded-xl border border-dashed border-border text-ink-500 text-xs">
                                                        No uploaded CVs or onboarding documents found for this user account.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 bg-surface border-t border-border flex justify-end">
                            <button
                                onClick={closeModal}
                                className="btn-secondary text-xs px-6 py-2"
                            >
                                Close Details
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
